import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { ScanErrorResponse } from 'services/face-scan/type';
import { PATH_ROUTES } from 'routes/path';
import { DialogDescription } from '@radix-ui/react-dialog';

interface ScanErrorProps {
  open: boolean;
  onClose: () => void;
  onRescan: () => void;
  error: ScanErrorResponse;
}

export default function ScanErrorDialog({
  open,
  onClose,
  onRescan,
  error,
}: ScanErrorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onContinue = () => navigate(PATH_ROUTES.FACE_SCAN_RESULT);

  const buttonConfig = [
    {
      condition: error?.rescan,
      onClick: onRescan,
      text: t('rescan'),
    },
    {
      condition: error?.continue,
      onClick: onContinue,
      text: t('continue'),
    },
    {
      condition: error?.exit_flag,
      onClick: onClose,
      text: t('exit'),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className='max-w-md px-4 py-6 sm:p-8'
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className='sr-only'>Scan Error</DialogTitle>
          <DialogDescription className='sr-only'>
            Scan error details
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-1'>
            <h2 className='font-semibold text-center sm:text-lg'>
              {t('error')}
            </h2>
            <p className='leading-tight'>{t('scan_error_info')}</p>
          </div>

          <div className='space-y-4'>
            {error?.error_msg && error.error_msg.length
              ? error.error_msg.map((err) => (
                  <div
                    className='max-w-lg space-y-1'
                    key={err.header}
                    data-testid='scan-error'
                  >
                    <h2 className='font-semibold'>{err?.header}</h2>
                    <p className='leading-tight text-gray-500'>
                      {err?.description}
                    </p>
                  </div>
                ))
              : null}
          </div>

          <div className='flex items-center justify-center space-y-4'>
            {buttonConfig.filter((button) => button.condition)?.length ? (
              <div className='flex items-center justify-center gap-x-4'>
                {buttonConfig
                  .filter((button) => button.condition)
                  .map(({ text, onClick }) => (
                    <Button
                      key={text}
                      onClick={onClick}
                      className='w-24 h-8 text-sm'
                    >
                      {text}
                    </Button>
                  ))}
              </div>
            ) : (
              <Button onClick={onClose} className='w-24 h-8 text-sm'>
                {t('ok')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
